import * as actions from '@actions';
import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'wrapped-redux';
import { useUserSafe } from '@vl-core/hooks/useUser';

export type Article = {
  title: string;
  body: string;
};

function unifyArticle(article: { title: string; template_body: string }): Article | undefined {
  if ('template_body' in article) {
    return { title: article.title, body: article.template_body };
  }

  return undefined;
}

async function getNoAuthArticle(category: string, dispatch: ReturnType<typeof useDispatch>) {
  const result = await dispatch(actions.fetchArticleNoAuthFromDB(category));
  return unifyArticle(result);
}

async function getAuthenticatedArticle(category: string, dispatch: ReturnType<typeof useDispatch>) {
  const article = await dispatch(actions.getArticleNo(category));

  if (!article?.confluence?.length) return undefined;

  const templateCode = article.confluence[0].content_template_code;

  const result = await dispatch(actions.fetchArticleFromDB(templateCode));

  return unifyArticle(result);
}

const commonCategories = [
  'cookies',
  'accessibility',
  'regulatory',
  'instructions_for_use',
  'clinician_instructions',
  'services',
  'restricted_access_services',
  'cookies_essential',
  'cookies_functional',
  'cookies_performance',
  'system_warning'
];

async function fetchArticle(
  user: {
    policies_accepted: boolean;
    user_type_code: string;
  },
  category: string,
  dispatch: ReturnType<typeof useDispatch>
) {
  const isCommonCategory = commonCategories.includes(category);

  try {
    if (!user || isCommonCategory) {
      return await getNoAuthArticle(category, dispatch);
    }

    const { policies_accepted, user_type_code } = user;
    const isClinician = user_type_code === 'CLINICIAN';

    if (policies_accepted || isClinician) {
      return await getAuthenticatedArticle(category, dispatch);
    }

    return undefined;
  } catch {
    return undefined;
  }
}

export default function useArticle(category: string, noAuthFallback = false): Article | undefined {
  const user = useUserSafe();
  const dispatch = useDispatch();
  const result = useQuery({
    queryKey: ['useArticle', category],
    queryFn: () => fetchArticle(user || { policies_accepted: false, user_type_code: '' }, category, dispatch),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!user || noAuthFallback
  });

  return result.data;
}
